
import React, { useEffect, useState } from 'react';

const steps = [
  { id: '01', title: 'Data Origination', desc: 'Secure entry from survey sources.' },
  { id: '02', title: 'Validation', desc: 'Automated logical consistency checks.' },
  { id: '03', title: 'Approval', desc: 'Digital sign-off by senior authorities.' },
  { id: '04', title: 'Publication', desc: 'Encrypted export to public portal.' },
];

const WorkflowTimeline: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);

  // Subtle cycling highlight to indicate "active flow"
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative py-16 overflow-hidden">
      {/* Background Connecting Line (Desktop) */}
      <div className="absolute top-1/2 left-0 w-full h-[2px] bg-white/5 hidden md:block -translate-y-1/2">
        <div 
          className="h-full bg-gradient-to-r from-transparent via-accent to-transparent w-32 absolute animate-slide-infinite opacity-50"
          style={{ animationDuration: '3s' }}
        ></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-12 relative z-10">
        {steps.map((step, idx) => {
          const isActive = idx === activeStep;
          
          return (
            <div 
              key={idx} 
              className="flex flex-col items-center text-center group animate-fade-in-up"
              style={{ animationDelay: `${idx * 150}ms` }}
            >
              <div className="relative mb-6">
                {/* Outer Pulse Ring */}
                {isActive && (
                  <div className="absolute inset-0 bg-accent/20 rounded-full animate-ping"></div>
                )}
                
                {/* Node Circle */}
                <div className={`
                  w-14 h-14 rounded-full border-2 flex items-center justify-center font-bold text-lg transition-all duration-700
                  ${isActive 
                    ? 'bg-accent text-primary border-accent shadow-[0_0_30px_rgba(34,211,238,0.6)] scale-110' 
                    : 'bg-primary border-white/10 text-slate-500 group-hover:border-accent/50 group-hover:text-accent shadow-xl'}
                `}>
                  {step.id}
                </div>

                {/* Vertical Line for Mobile */}
                {idx < steps.length - 1 && (
                  <div className="absolute top-14 left-1/2 -translate-x-1/2 w-px h-12 bg-white/5 md:hidden">
                     {isActive && <div className="w-full bg-accent animate-grow-v h-full origin-top"></div>}
                  </div>
                )}
              </div>

              <h4 className={`font-bold mb-2 transition-colors duration-500 ${isActive ? 'text-accent' : 'text-white'}`}>
                {step.title}
              </h4>
              <p className="text-slate-500 text-xs max-w-[160px] leading-relaxed group-hover:text-slate-400 transition-colors">
                {step.desc}
              </p>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes slide-infinite {
          from { transform: translateX(-100%); }
          to { transform: translateX(1000%); }
        }
        @keyframes grow-v {
          from { transform: scaleY(0); }
          to { transform: scaleY(1); }
        }
        .animate-slide-infinite {
          animation: slide-infinite linear infinite;
        }
        .animate-grow-v {
          animation: grow-v 3s infinite;
        }
      `}</style>
    </div>
  );
};

export default WorkflowTimeline;
